/** PersonSheet: everything known about one person, and the things done to them: log a call, add a note, mark reviewed. J/K walk the list behind it, R reviews. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { prefetch, useQuery } from "../data/useQuery";
import { Badge, Button, cx, Icon, Kbd, Sheet, SheetSection, SkeletonText, Standing, Tooltip, type StandingValue } from "../kit";
import { neighbour, useRowFacts, useSelection } from "../screens/people/selection";
import { LoadError } from "../app/LoadError";
import { AcFields, acKey, loadAc } from "./AcFields";
import { AddNote } from "./AddNote";
import { History } from "./History";
import { loadPerson, personKey } from "./load";
import { LogCall } from "./LogCall";
import { SameName } from "./Namesakes";
import { doneWords, callSummary, NO_CALL, type CallDraft, type CaseKind } from "./review";
import { restoreTarget, revealRow, rowSeed } from "./rows";
import { addedWords } from "./format";
import { CaseSection, Sections } from "./Sections";
import { useCase } from "./useCase";

export interface PersonSheetProps {
  id: number;
  open: boolean;
  onNavigate: (id: number) => void;
  onClose: () => void;
  /** The person is not on the server: the host drops ?person= and says so. */
  onMissing: () => void;
  isReviewed: (id: number, server: boolean) => boolean;
  onReview: (id: number, kind: CaseKind, queueKey: string, call?: string) => void;
  onUndo: (id: number, ackId?: number) => void;
}

const EDITABLE = /^(INPUT|TEXTAREA|SELECT)$/;

function isEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || EDITABLE.test(el.tagName));
}

/** The second click of a double-click: it lands on whatever replaced the button, and means nothing. */
const repeated = (event: { detail: number }) => event.detail > 1;

export function PersonSheet({ id, open, onNavigate, onClose, onMissing, isReviewed, onReview, onUndo }: PersonSheetProps) {
  const marker = useRef<HTMLDivElement>(null);
  const query = useQuery(personKey(id), () => loadPerson(id));
  // Its ActiveCampaign section shows once the person has, but is asked for now: the sheet lands in one round (#714).
  useEffect(() => prefetch(acKey(id), () => loadAc(id)), [id]);

  // Hold the last person while the next loads, so J/K never flashes a skeleton or empties the sheet.
  const [held, setHeld] = useState<typeof query.data>();
  if (query.data && query.data !== held) setHeld(query.data);
  const loaded = query.data ?? (query.error ? undefined : held);
  const person = loaded?.person;
  const settled = !!query.data;
  // The row the sheet was opened from says who this is before the data does.
  const seed = useMemo(() => rowSeed(id), [id]);
  const missing = (query.error as { status?: number } | null)?.status === 404;

  // The row the list shows is the truth for this person: review and the licence end repeat it.
  const facts = useRowFacts(id);
  const { viewId, ids } = useSelection();
  // People is the page but its rows have not landed (a deep link): they may show the district, so the sheet waits instead of pushing everything up when they do.
  const { pathname } = useLocation();
  const listPending = /\/people\/?$/.test(pathname) && ids.length === 0;
  const kase = useCase(id, settled ? person : undefined, viewId, facts);
  const { queueKey } = kase;
  // Everything below the name lands together: the person, and the lists that may still place them in a case.
  const ready = settled && !kase.settling;
  const ack = ready ? person?.acknowledgements.items.find((a) => a.is_active && a.queue_key === queueKey) : undefined;
  const reviewed = ready && !!queueKey && isReviewed(id, !!ack || !!facts?.done);

  // The call being logged: kept here so Mark reviewed can carry it, and dropped with the person.
  const [draft, setDraft] = useState<CallDraft>(NO_CALL);
  const [draftFor, setDraftFor] = useState(id);
  if (draftFor !== id) {
    setDraftFor(id);
    setDraft(NO_CALL);
  }

  // A call that comes back goes to the person it was logged on, if the sheet is still on them and nothing has been picked since.
  const restoreCall = (personId: number, call: CallDraft) => {
    if (latest.current.id === personId) setDraft((now) => (now.outcome ? now : call));
  };

  // The sheet stays on the person, who stays in the list: J goes on.
  const review = () => {
    if (!open || !ready || reviewed || !queueKey || !kase.kind) return;
    onReview(id, kase.kind, queueKey, draft.outcome ? callSummary(draft) : undefined);
    setDraft(NO_CALL);
  };
  const undo = () => onUndo(id, ack?.id);
  // J and K asked for and not yet shown: presses that come faster than the page renders walk on from there, not from the person still on screen.
  const asked = useRef<number[]>([]);
  useEffect(() => {
    const at = asked.current.indexOf(id);
    asked.current = at < 0 ? [] : asked.current.slice(at + 1);
  }, [id]);
  const step = (by: 1 | -1) => {
    const next = neighbour(asked.current.at(-1) ?? id, by);
    if (next === null) return;
    asked.current.push(next);
    onNavigate(next);
  };

  // Undo from the toast: the toast closes under the pointer and focus would fall to the page. It goes back to what the sheet offers next, or to the row when the sheet is closed.
  const wasReviewed = useRef(reviewed);
  useEffect(() => {
    if (wasReviewed.current && !reviewed) {
      const active = document.activeElement;
      if (!active || active === document.body || active.closest("[data-slot=toast]")) {
        const next = open ? marker.current?.closest("[role=dialog]")?.querySelector<HTMLElement>("footer button") : restoreTarget(id);
        next?.focus();
      }
    }
    wasReviewed.current = reviewed;
  }, [reviewed, open, id]);

  // Keep the latest handlers for the window listener without re-binding it every render.
  const latest = useRef({ id, open, review, step, onMissing });
  useEffect(() => {
    latest.current = { id, open, review, step, onMissing };
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const now = latest.current;
      if (!now.open || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || isEditable(event.target)) return;
      // The palette, a dialog or a menu owns the keyboard while it has focus; Esc is the sheet's own.
      const layer = (event.target as HTMLElement | null)?.closest?.('[role="dialog"], [role="menu"], [role="listbox"]');
      if (layer && layer !== marker.current?.closest('[role="dialog"]')) return;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      if (key === "j" || key === "ArrowDown" || key === "k" || key === "ArrowUp") {
        event.preventDefault();
        now.step(key === "j" || key === "ArrowDown" ? 1 : -1);
      } else if (key === "r") {
        event.preventDefault();
        now.review();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // J and K walk a list longer than the screen: the row for this person stays in view.
  useEffect(() => {
    if (open) revealRow(id);
  }, [id, open]);

  useEffect(() => {
    if (missing && open) latest.current.onMissing();
  }, [missing, open]);

  const identity = person?.identity;
  // The buttons are there from the first frame on a page that lists people, so the header does not move when the list lands.
  const listed = neighbour(id, 1) !== null || neighbour(id, -1) !== null || ((listPending || pathname === "/") && ids.length === 0);

  const footer = !ready || !queueKey ? undefined : reviewed ? (
    <Button variant="surface" onClick={(event) => !repeated(event) && undo()}>
      Undo review
    </Button>
  ) : (
    <Button variant="primary" aria-keyshortcuts="R" onClick={(event) => !repeated(event) && review()}>
      {doneWords(kase.kind).button}
      <span aria-hidden>
        <Kbd tone="dark">R</Kbd>
      </span>
    </Button>
  );

  return (
    <Sheet
      open={open && !missing}
      onClose={onClose}
      busy={!ready}
      title={<span data-name>{(settled ? identity?.display_name : seed?.name) ?? identity?.display_name ?? (query.error ? "Person" : <SkeletonText width="16ch" />)}</span>}
      // text-secondary-foreground: the sheet frame is bg-muted, where the description's own muted ink is 4.47:1.
      description={ready && identity ? <span className="text-secondary-foreground">{[identity.added_in_keystone ? addedWords(identity.added_in_keystone) : `ID ${identity.ce21_customer_id ?? identity.id}`, identity.ce21_account_deleted && "CE21 account deleted", kase.reason].filter(Boolean).join(" · ")}</span> : query.error ? undefined : <SkeletonText width="12ch" />}
      actions={
        listed ? (
          <div className="flex items-center">
            <Tooltip content="Next person (J)">
              <Button variant="ghost" size="icon-sm" aria-label="Next person" aria-keyshortcuts="J" disabled={neighbour(id, 1) === null} onClick={() => step(1)}>
                <Icon name="chevron-down" />
              </Button>
            </Tooltip>
            <Tooltip content="Previous person (K)">
              <Button variant="ghost" size="icon-sm" aria-label="Previous person" aria-keyshortcuts="K" disabled={neighbour(id, -1) === null} onClick={() => step(-1)}>
                <Icon name="chevron-up" />
              </Button>
            </Tooltip>
          </div>
        ) : undefined
      }
      footer={footer}
      restoreFocus={() => restoreTarget(id)}
    >
      <div ref={marker} className={cx("flex flex-col", person && !settled && "opacity-60")}>
        {query.error && !person ? (
          <LoadError error={query.error} onRetry={() => void query.refetch()} />
        ) : person && (ready || !settled) ? (
          <>
            <SheetSection>
              <div className="flex items-center justify-between gap-2">
                <Standing value={person.standing.standing} />
                {reviewed && <Badge tone="success">{doneWords(kase.kind).badge}</Badge>}
              </div>
            </SheetSection>
            {ready && <CaseSection person={person} kase={kase} />}
            {ready && !kase.held && <SameName namesakes={person.namesakes ?? []} />}
            {ready && <LogCall personId={id} queueKey={queueKey ?? undefined} draft={draft} onDraft={setDraft} onRestore={restoreCall} />}
            <Sections person={person} districts={loaded.districts} addresses={loaded.addresses} facts={facts} kase={kase} listPending={listPending} />
            {ready && <AddNote key={`note-${id}`} personId={id} />}
            <History key={`history-${id}`} person={person} />
            {ready && <AcFields key={`ac-${id}`} id={id} />}
          </>
        ) : (
          <BodySkeleton standing={settled ? person?.standing.standing : seed?.standing} />
        )}
      </div>
    </Sheet>
  );
}

function RowSkeleton() {
  return (
    <div className="flex items-baseline justify-between gap-4 text-body leading-none">
      <SkeletonText width="9ch" />
      <SkeletonText width="14ch" />
    </div>
  );
}

/** The sections every person has, in the same boxes, before the data lands. The row's own standing is already known. The bars wait a beat (80 ms, then a 120 ms fade: in by 200 ms), so a quick load never shows them. */
function BodySkeleton({ standing }: { standing: StandingValue | null | undefined }) {
  return (
    <>
      <SheetSection>{standing ? <Standing value={standing} /> : <SkeletonText width="10ch" className="text-body" />}</SheetSection>
      <div aria-hidden className="animate-fade [animation-duration:120ms]" style={{ animationDelay: "80ms" }}>
        {["Contact", "Membership", "Licence"].map((title, i) => (
          <SheetSection key={title} title={title}>
            <dl className="flex w-full flex-col gap-3">
              {Array.from({ length: 3 - (i === 2 ? 1 : 0) }, (_, j) => (
                <RowSkeleton key={j} />
              ))}
            </dl>
          </SheetSection>
        ))}
      </div>
    </>
  );
}
